import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { isParent } from "@/lib/roles";
import { UsersThree } from "@phosphor-icons/react";

/** Guardians, pickup authorization and emergency contacts. Management lives in Families (Phase 2). */
export const FamilyTab = ({ student, user }) => (
  <div className="grid lg:grid-cols-2 gap-4" data-testid="family-tab">
    <div className="bg-white rounded-2xl border border-border p-6" data-testid="guardian-list">
      <div className="flex justify-between items-start gap-3">
        <h2 className="font-display text-lg font-semibold">Parents & guardians</h2>
        {!isParent(user) && (
          <Link to={`/admin/families?student=${student.id}`}><Button size="sm" variant="outline" className="rounded-full font-bold" data-testid="manage-family-btn">
            <UsersThree size={15} className="mr-1" /> Manage family</Button></Link>
        )}
      </div>
      {student.guardians.length === 0 && <p className="mt-4 text-sm text-muted-foreground">No guardians connected yet.</p>}
      <div className="mt-4 space-y-3">
        {student.guardians.map((g, i) => (
          <div key={g.id || g.email || i} className="rounded-2xl border border-border p-4" data-testid={`guardian-${g.email}`}>
            <div className="flex flex-wrap gap-2 items-center">
              <p className="font-bold">{g.name || g.email}</p>
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-muted">{g.relationship}</span>
              {g.legal_guardian && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">Legal guardian</span>}
              {g.permission_level && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 capitalize" data-testid={`guardian-level-${g.email}`}>{g.permission_level.replace("_", "-")} access</span>}
              {g.billing_responsible && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">Billing contact</span>}
              {g.pickup_authorized ? <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">Pickup authorized</span>
                : <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-600">Not authorized for pickup</span>}
              {g.emergency_priority && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">Emergency #{g.emergency_priority}</span>}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{g.email}{g.phone ? ` · ${g.phone}` : ""}</p>
            {g.custody_restrictions && !isParent(user) && <p className="mt-2 text-sm font-semibold text-red-600">Restriction: {g.custody_restrictions}</p>}
            {g.implicit && !isParent(user) && <p className="mt-2 text-xs text-muted-foreground">Linked from the student's parent email — invite them from Families to set individual permissions.</p>}
          </div>
        ))}
      </div>
    </div>
    <div className="bg-white rounded-2xl border border-border p-6" data-testid="emergency-panel">
      <h2 className="font-display text-lg font-semibold">Emergency contact</h2>
      <p className="mt-3 font-bold">{student.emergency_contact || <span className="text-muted-foreground font-semibold">None on file</span>}</p>
      <p className="mt-1 text-sm text-muted-foreground">Parent phone: {student.parent_phone || "—"}</p>
      <h3 className="mt-6 font-bold text-sm">Approved pickups</h3>
      <ul className="mt-2 space-y-1 text-sm">
        {student.guardians.filter((g) => g.pickup_authorized).map((g, i) => <li key={g.email || i}>• {g.name || g.email}</li>)}
        {student.guardians.filter((g) => g.pickup_authorized).length === 0 && <li className="text-muted-foreground">No one is authorized yet.</li>}
      </ul>
      {!isParent(user) && student.guardians.some((g) => !g.pickup_authorized && g.verification_status) && (
        <div className="mt-5 rounded-2xl border-2 border-[#FBBF24] bg-amber-50/70 p-4" data-testid="pending-pickup-list">
          <p className="text-sm font-bold text-amber-800">Pending verification — do not release a child to these adults</p>
          <ul className="mt-2 space-y-2 text-sm">
            {student.guardians.filter((g) => !g.pickup_authorized && g.verification_status).map((g) => (
              <li key={g.id || g.email} data-testid={`pending-pickup-${g.id || g.email}`}>
                • <strong>{g.name || g.email}</strong> — {g.relationship}
                <span className="block text-xs text-muted-foreground">
                  Source: {g.source === "brightwheel_import" ? "Brightwheel roster import" : g.source || "manual"}
                  {" · "}not yet verified by an administrator
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  </div>
);
